import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-gods-love",
  title: "10 Bible verses about God's love, and what each one proves it by",
  h1: "Bible verses about God's love",
  description:
    "Ten passages from the World English Bible with a note on each — because almost every one of them points at an action rather than describing a feeling.",
  targets: [
    "bible verses about god's love",
    "scripture on the love of god",
    "verses about how much god loves us",
  ],
  published: "2026-09-03",
  standfirst:
    "Nearly every passage here defines the love by something that was done rather than by how strong it is, which is what makes them usable on a day you feel nothing.",
  takeaways: [
    "The New Testament almost never asserts the love as a quantity. It points at an event and says: that is what it is.",
    "Romans 5:8 puts the timing at the centre — while we were yet sinners, meaning before anything changed rather than after.",
    "1 John 4:10 is explicit that the direction runs the other way: not that we loved God, but that he loved us.",
    "A verse about how much you are loved is hard to hold on a flat day. A verse about something that happened is not affected by how you feel about it.",
  ],
  intro: [
    {
      type: "p",
      text: "God's love is tenth on the most-searched topic list of the largest topical Bible site, and love is the single most-searched term on both of the two biggest Bible platforms — the all-time leader on one of them across twenty-five years. It is the most common thing anybody comes to Scripture looking for.",
    },
    {
      type: "p",
      text: "What the passages themselves do is more interesting than the volume suggests. Almost none of them describe an emotion; they point at an act — a sending, a death, a drawing — and define the love by it. That is also true of {the most famous verse of the lot}, where the word everybody reads as an intensifier is actually a manner word.",
      links: { "the most famous verse of the lot": "/verse/john-3-16" },
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: "But God commends his own love toward us, in that while we were yet sinners, Christ died for us.",
        ref: "Romans 5:8",
      },
      note: "The timing is the whole argument. Not once we had improved, not on condition of anything — while we were yet, which places the act before any change on our side. Commends is a demonstration word: Paul is describing something put on display as evidence, which is why the sentence works as proof rather than as reassurance.",
    },
    {
      verse: {
        text: "In this is love, not that we loved God, but that he loved us, and sent his Son as the atoning sacrifice for our sins.",
        ref: "1 John 4:10",
      },
      note: "An explicit correction of the direction, and it is aimed at a natural mistake: assuming the relationship begins with our reaching. John says it does not. The sentence also defines love by a specific event rather than describing it, which is the pattern running through this whole collection.",
    },
    {
      verse: {
        text: "For I am persuaded that neither death, nor life, nor angels, nor principalities, nor things present, nor things to come, nor powers, nor height, nor depth, nor any other created thing will be able to separate us from God's love which is in Christ Jesus our Lord.",
        ref: "Romans 8:38–39",
      },
      note: "A list constructed to be exhaustive, and the final item closes the loophole: nor any other created thing. Paul is writing to people for whom several of the named items were live threats. The claim is not that the listed things will not happen — most of them did happen to him — but that they cannot achieve separation.",
    },
    {
      verse: {
        text: "See how great a love the Father has given to us, that we should be called children of God! For this cause the world doesn't know us, because it didn't know him.",
        ref: "1 John 3:1",
      },
      note: "The measure offered is a change of legal status rather than a feeling: called children, which in the ancient world is an adoption term with inheritance attached. The second half is unexpected and worth keeping — being unrecognised is presented as a consequence of the new status rather than as evidence against it.",
    },
    {
      verse: {
        text: "Yes, I have loved you with an everlasting love. Therefore I have drawn you with loving kindness.",
        ref: "Jeremiah 31:3",
      },
      note: "Said to a nation in the middle of the book that spends most of its length announcing their destruction, which is exactly what gives it force. The therefore is the interesting word: the drawing is presented as the consequence of the love rather than as its cause, and the chapter it opens goes on to promise the new covenant.",
    },
    {
      verse: {
        text: "By this God's love was revealed in us, that God has sent his only born Son into the world that we might live through him.",
        ref: "1 John 4:9",
      },
      note: "Revealed rather than declared — the love is treated as something made visible by an event rather than announced. The construction is the same one John's gospel uses, and the effect is that anybody asking what the love is like is pointed at a life rather than given an adjective.",
    },
    {
      verse: {
        text: "Even as the Father has loved me, I also have loved you. Remain in my love.",
        ref: "John 15:9",
      },
      note: "The comparison is startling if it is read slowly: the love being described is the same in kind as the one inside the Trinity. And the instruction attached is spatial — remain, stay put — which treats the love as a place to live rather than as a feeling to maintain. It is said hours before the arrest.",
    },
    {
      verse: {
        text: "may be strengthened to comprehend with all the saints what is the width and length and height and depth, and to know Christ's love which surpasses knowledge, that you may be filled with all the fullness of God.",
        ref: "Ephesians 3:18–19",
      },
      note: "Paul asks for the strength to grasp something and then says in the same breath that it exceeds grasping. That is not confusion; it is the point. Note also with all the saints — the comprehension being prayed for is collective, which is an odd and deliberate thing to say about something usually treated as private.",
    },
    {
      verse: {
        text: "Give thanks to Yahweh, for he is good, for his loving kindness endures forever.",
        ref: "Psalm 136:1",
      },
      note: "The opening of a psalm that repeats the second clause after every single line for twenty-six verses, through creation, the exodus, the sea and the wilderness. The repetition is liturgical and it is also the argument: the same refrain attached to disaster and to rescue alike, which is a claim about constancy rather than about mood.",
    },
    {
      verse: {
        text: "Yahweh, your God, is among you, a mighty one who will save. He will rejoice over you with joy. He will calm you in his love. He will rejoice over you with singing.",
        ref: "Zephaniah 3:17",
      },
      note: "The only place in Scripture where God is described as singing, and it arrives after two and a half chapters of unrelieved judgement. The reversal is abrupt on purpose. Held on its own it is a lovely sentence; held where it sits, it is a considerably stronger one.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "how-to-use-these",
      text: "How to use these",
    },
    {
      type: "p",
      text: "On a day the feeling is not available — which is most days for most people — read the ones that name an event rather than the ones that describe a state. Romans 5:8 and 1 John 4:10 are both immune to how you feel about them, because neither is describing you.",
    },
    {
      type: "p",
      text: "And if nothing has felt like anything for months, in prayer or out of it, the flatness may be medical rather than spiritual. That responds to help, and {saying so plainly} is a better first move than a longer list of verses.",
      links: { "saying so plainly": "/prayers/prayer-when-you-cant-pray" },
    },
    {
      type: "note",
      text: "Psalm 136 is worth reading aloud once, all twenty-six verses, refrain included. The repetition does something a single verse cannot.",
    },
  ],
  faq: [
    {
      q: "What is the best Bible verse about God's love?",
      a: "Romans 5:8 if you want the argument — the timing, while we were yet sinners, is the whole point. 1 John 4:10 if you want the direction corrected: not that we loved God, but that he loved us.",
    },
    {
      q: "How does the Bible describe God's love?",
      a: "Almost always by pointing at an action rather than by describing a feeling — a sending, a death, a drawing, an adoption. That is what makes the passages usable on a day when nothing is felt.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so all ten are printed whole rather than referred to. Jeremiah 31:3, Psalm 136:1 and Zephaniah 3:17 render the divine name as Yahweh, where most translations have 'the LORD'.",
    },
  ],
  related: ["how-to-memorize-bible-verses", "phone-addiction-bible-verses"],
  relatedTopics: ["bible-verses-about-forgiveness", "bible-verses-about-peace"],
};
